/**
 * Contact page skeleton loaders.
 *
 * ContactFormLayoutSkeleton matches ContactForm grid (info + fields).
 * ContactPageSkeleton matches ContactPageClient: hero, auth CTA, divider, form.
 */

"use client";

import { Box, Flex, Skeleton } from "@chakra-ui/react";
import { MicroSiteHeaderSkeleton } from "@/components/feedback";
import { useThemeTokens } from "@/lib/theme/tokens";

function PublicHeroSkeleton() {
  return (
    <Box>
      <Skeleton height="12px" width="96px" mb={3} borderRadius="4px" />
      <Skeleton height={{ base: "34px", md: "42px" }} width="72%" mb={3} borderRadius="6px" />
      <Skeleton height="16px" width="100%" mb={2} borderRadius="4px" />
      <Skeleton height="16px" width="88%" borderRadius="4px" />
    </Box>
  );
}

function FormFieldSkeleton({
  labelWidth,
  controlHeight = "44px",
}: {
  labelWidth: string;
  controlHeight?: string;
}) {
  return (
    <Box>
      <Skeleton height="14px" width={labelWidth} mb={2} borderRadius="4px" />
      <Skeleton height={controlHeight} width="100%" borderRadius="8px" />
    </Box>
  );
}

function ContactAuthCtaSkeleton({ variant = "portal" }: { variant?: "public" | "portal" }) {
  const theme = useThemeTokens();

  return (
    <Box
      pt={variant === "public" ? { base: 1, md: 2 } : 6}
      borderTopWidth="1px"
      borderColor={variant === "public" ? theme.borderGlass.subtle : "transparent"}
      maxW={variant === "public" ? "100%" : "2xl"}
    >
      <Flex direction="column" gap={2} mb={4}>
        <Skeleton height="16px" width="100%" maxW="480px" borderRadius="4px" />
        <Skeleton height="16px" width="75%" maxW="360px" borderRadius="4px" />
      </Flex>
      <Flex gap={3} flexDirection={{ base: "column", sm: "row" }}>
        <Skeleton height="48px" width={{ base: "100%", sm: "140px" }} borderRadius="8px" />
        <Skeleton height="48px" width={{ base: "100%", sm: "140px" }} borderRadius="8px" />
      </Flex>
    </Box>
  );
}

function ContactFormFieldsSkeleton({ variant = "portal" }: { variant?: "public" | "portal" }) {
  const controlHeight = variant === "public" ? "48px" : "44px";

  return (
    <Flex direction="column" gap={variant === "public" ? 5 : 4} w="100%">
      <FormFieldSkeleton labelWidth="72px" controlHeight={controlHeight} />
      <FormFieldSkeleton labelWidth="40px" controlHeight={controlHeight} />
      <FormFieldSkeleton labelWidth="112px" controlHeight={controlHeight} />
      <FormFieldSkeleton labelWidth="60px" controlHeight={variant === "public" ? "144px" : "120px"} />
      <Skeleton height={controlHeight} width="100%" borderRadius="8px" />
    </Flex>
  );
}

function ContactInfoSkeleton({
  variant = "portal",
  portalLayout = "sidebar",
}: {
  variant?: "public" | "portal";
  portalLayout?: "sidebar" | "compact";
}) {
  if (variant === "portal" && portalLayout === "compact") {
    return (
      <Flex align="center" gap={3} w="100%">
        <Skeleton height="48px" width="48px" borderRadius="10px" flexShrink={0} />
        <Box flex={1} minW={0}>
          <Skeleton height="18px" width="55%" mb={2} borderRadius="4px" />
          <Skeleton height="14px" width="40%" mb={2} borderRadius="4px" />
          <Skeleton height="12px" width="80%" borderRadius="4px" />
        </Box>
      </Flex>
    );
  }

  const logoSize = variant === "public" ? "96px" : "72px";

  return (
    <Flex direction="column" gap={4}>
      <Skeleton height={logoSize} width={logoSize} borderRadius="10px" flexShrink={0} />
      <Skeleton height="18px" width="168px" borderRadius="4px" />
      <Box>
        <Skeleton height="12px" width="56px" mb={2} borderRadius="4px" />
        <Skeleton height="16px" width="100%" maxW="200px" mb={1.5} borderRadius="4px" />
        <Skeleton height="16px" width="72%" maxW="160px" mb={4} borderRadius="4px" />
        <Skeleton height="12px" width="48px" mb={2} borderRadius="4px" />
        <Skeleton height="16px" width="120px" borderRadius="4px" />
      </Box>
    </Flex>
  );
}

function PublicContactFormLayoutSkeleton() {
  const theme = useThemeTokens();

  return (
    <Flex
      direction={{ base: "column", lg: "row" }}
      gap={{ base: 6, lg: 8 }}
      w="100%"
    >
      <Box
        flex={{ lg: 1 }}
        minW={0}
        w="100%"
        pb={{ base: 6, lg: 0 }}
        borderBottomWidth={{ base: "1px", lg: 0 }}
        borderRightWidth={{ lg: "1px" }}
        borderColor={theme.borderGlass.subtle}
        pr={{ lg: 8 }}
      >
        <Skeleton height="12px" width="56px" mb={5} borderRadius="4px" />
        <ContactInfoSkeleton variant="public" />
      </Box>
      <Box flex={{ lg: 2 }} minW={0} pt={{ lg: 1 }}>
        <Skeleton height="12px" width="72px" mb={5} borderRadius="4px" />
        <ContactFormFieldsSkeleton variant="public" />
      </Box>
    </Flex>
  );
}

function PortalContactFormLayoutSkeleton() {
  const theme = useThemeTokens();

  return (
    <Box
      data-testid="contact-form-skeleton"
      w="100%"
      borderRadius="12px"
      borderWidth="1px"
      borderColor={theme.borderGlass.default}
      overflow="hidden"
    >
      <Box p={{ base: 5, md: 8 }}>
        <Box display={{ base: "none", md: "block" }} w="100%">
          <Flex direction="row" gap={8} w="100%" align="flex-start">
            <Box
              flex="0 0 220px"
              pr={8}
              borderRightWidth="1px"
              borderColor={theme.borderGlass.subtle}
            >
              <Skeleton height="12px" width="56px" mb={5} borderRadius="4px" />
              <ContactInfoSkeleton variant="portal" portalLayout="sidebar" />
            </Box>
            <Box flex={1} minW={0}>
              <Skeleton height="12px" width="72px" mb={5} borderRadius="4px" />
              <ContactFormFieldsSkeleton variant="portal" />
            </Box>
          </Flex>
        </Box>

        <Flex direction="column" gap={5} w="100%" display={{ base: "flex", md: "none" }}>
          <ContactInfoSkeleton variant="portal" portalLayout="compact" />
          <Box borderTopWidth="1px" borderColor={theme.borderGlass.subtle} />
          <ContactFormFieldsSkeleton variant="portal" />
        </Flex>
      </Box>
    </Box>
  );
}

/** Skeleton for ContactForm layout (desktop grid + mobile stack). */
export function ContactFormLayoutSkeleton({
  variant = "portal",
}: {
  variant?: "public" | "portal";
}) {
  if (variant === "public") {
    return <PublicContactFormLayoutSkeleton />;
  }

  return <PortalContactFormLayoutSkeleton />;
}

export const ContactPageSkeleton = () => {
  const theme = useThemeTokens();

  return (
    <Flex
      direction="column"
      gap={{ base: 6, md: 8 }}
      w="100%"
    >
      <PublicHeroSkeleton />
      <ContactAuthCtaSkeleton variant="public" />
      <Box pt={{ base: 1, md: 2 }} borderTopWidth="1px" borderColor={theme.borderGlass.subtle}>
        <PublicContactFormLayoutSkeleton />
      </Box>
    </Flex>
  );
};
